<script setup lang="ts">
import { onMounted, computed, ref, watch, markRaw } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import { dateComparator, formatDate } from '@/utils/dateUtils'
import ColumnSelector from '@/components/data-visualization/grids/ColumnSelector.vue'
import TodoModal from '@/components/features/todos/TodoModal.vue'
import TodoGridActions from '@/components/features/todos/TodoGridActions.vue'
import TodoStatusBadge from '@/components/features/todos/TodoStatusBadge.vue'
import TodoRelationCell from '@/components/features/todos/TodoRelationCell.vue'
import { useTodoStore } from '@/stores/useTodoStore'
import type { ColumnConfig } from '@/components/data-visualization/grids/ColumnSelector.vue'

const todoStore = useTodoStore()

const todos = ref<any[]>([])
const filters = ref({ search: '', type: '', status: '', priority: '', dueDateFrom: '', dueDateTo: '' })
const pageSize = ref<number>(50)
const page = ref<number>(1)
const totalTodos = ref<number>(0)
const totalPages = ref<number>(1)
const selectedTodo = ref<any | null>(null)
const showModal = ref(false)
const copyTodoData = ref<any | null>(null)
const showCopyModal = ref(false)
const sort = ref<{ field: string; direction: string } | null>(null)

const SORTABLE_FIELDS = new Set(['title', 'status', 'priority', 'type', 'dueDate'])

const allColumns = computed<ColumnConfig[]>(() => [
  {
    field: 'uuid',
    headerName: '#',
    visible: false,
    locked: true,
  },
  {
    field: 'title',
    headerName: 'Titre',
    visible: true,
  },
  {
    field: 'type',
    headerName: 'Type',
    visible: true,
  },
  {
    field: 'relation',
    headerName: 'Contact / Entreprise',
    visible: true,
  },
  {
    field: 'description',
    headerName: 'Description',
    visible: true,
  },
  {
    field: 'status',
    headerName: 'Statut',
    visible: true,
  },
  {
    field: 'priority',
    headerName: 'Priorité',
    visible: true,
  },
  {
    field: 'dueDate',
    headerName: 'Échéance',
    visible: true,
  },
])

const visibleColumnFields = ref<string[]>([])

let timeout: any = null

const gridColumns = computed(() => {
  const visibleFields = new Set(visibleColumnFields.value)
  return [
    {
      field: 'title',
      headerName: 'Titre',
      cellClass: 'border-0',
      filter: false,
      hide: !visibleFields.has('title'),
    },
    {
      field: 'type',
      headerName: 'Type',
      cellClass: 'border-0',
      filter: false,
      hide: !visibleFields.has('type'),
      valueFormatter: (params: any) => {
        const typeLabels: Record<string, string> = {
          TASK: 'Tâche',
          REMINDER: 'Rappel',
          CALL: 'Appel',
          EMAIL: 'Email',
          MEETING: 'Réunion',
          FOLLOW_UP: 'Suivi',
        }
        return typeLabels[params.value] || params.value || '-'
      },
    },
    {
      field: 'relation',
      headerName: 'Contact / Entreprise',
      cellClass: 'border-0',
      filter: false,
      hide: !visibleFields.has('relation'),
      cellRenderer: markRaw(TodoRelationCell),
      valueGetter: (params: any) => {
        const contact = params.data?.contact
        const company = params.data?.company
        if (contact) {
          return `${contact.firstname || ''} ${contact.lastname || ''}`.trim()
        }
        if (company) {
          return company.name || '-'
        }
        return '-'
      },
    },
    {
      field: 'description',
      headerName: 'Description',
      cellClass: 'border-0',
      filter: false,
      hide: !visibleFields.has('description'),
    },
    {
      field: 'status',
      headerName: 'Statut',
      cellClass: 'border-0',
      filter: false,
      hide: !visibleFields.has('status'),
      cellRenderer: markRaw(TodoStatusBadge),
    },
    {
      field: 'priority',
      headerName: 'Priorité',
      cellClass: 'border-0',
      filter: false,
      hide: !visibleFields.has('priority'),
      valueFormatter: (params: any) => {
        const priorityLabels: Record<string, string> = {
          LOW: 'Basse',
          MEDIUM: 'Moyenne',
          HIGH: 'Haute',
          URGENT: 'Urgente',
        }
        return priorityLabels[params.value] || params.value || '-'
      },
    },
    {
      field: 'dueDate',
      headerName: 'Échéance',
      cellClass: 'border-0',
      filter: false,
      hide: !visibleFields.has('dueDate'),
      valueFormatter: (params: any) => {
        if (!params.value) return '-'
        return formatDate(params.value?.toString() || '')
      },
      comparator: dateComparator,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      resizable: false,
      filter: false,
      sortable: false,
      minWidth: 150,
      maxWidth: 200,
      cellClass: 'border-0 grid-action',
      cellRenderer: markRaw(TodoGridActions),
      cellRendererParams: {
        tooltipPosition: 'left',
        onClickView: (todo: any) => {
          handleViewTodo(todo)
        },
      },
    },
  ]
})

watch(
  filters,
  () => {
    page.value = 1
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => {
      fetchTodos()
    }, 500)
  },
  { deep: true },
)

onMounted(async () => {
  await fetchTodos()
})

async function fetchTodos() {
  const cleanFilters: Record<string, any> = {}
  if (filters.value.search) cleanFilters.search = filters.value.search
  if (filters.value.type) cleanFilters.type = filters.value.type
  if (filters.value.status) cleanFilters.status = filters.value.status
  if (filters.value.priority) cleanFilters.priority = filters.value.priority
  if (filters.value.dueDateFrom) cleanFilters.dueDateFrom = filters.value.dueDateFrom
  if (filters.value.dueDateTo) cleanFilters.dueDateTo = filters.value.dueDateTo
  if (sort.value) cleanFilters.sort = [sort.value]

  const result = await todoStore.fetchTodos(cleanFilters, pageSize.value, page.value)
  if (result) {
    todos.value = result.data || []
    totalTodos.value = result.total || 0
    totalPages.value = result.totalPages || 1
  }
}

function onTodoCreated() {
  fetchTodos()
}

function handleViewTodo(todo: any) {
  selectedTodo.value = todo
  showModal.value = true
}

function closeModal() {
  selectedTodo.value = null
  showModal.value = false
}

function onTodoUpdated() {
  fetchTodos()
  closeModal()
}

function onCompleteAndCopy(data: any) {
  fetchTodos()
  closeModal()
  copyTodoData.value = data
  showCopyModal.value = true
}

function closeCopyModal() {
  copyTodoData.value = null
  showCopyModal.value = false
}

function onCopyCreated() {
  fetchTodos()
  closeCopyModal()
}

function onColumnsChanged(newVisibleColumns: string[]) {
  visibleColumnFields.value = newVisibleColumns
}

function onSortChanged(sortEvent: { colId: string; sort: string } | null) {
  if (sortEvent && SORTABLE_FIELDS.has(sortEvent.colId)) {
    sort.value = { field: sortEvent.colId, direction: sortEvent.sort }
  } else {
    sort.value = null
  }
  fetchTodos()
}

async function onPaginationChanged(newPageSize: number, newPage: number) {
  if (pageSize.value === newPageSize && page.value === newPage) return
  let changed = false
  if (pageSize.value !== newPageSize) {
    pageSize.value = newPageSize
    changed = true
  }
  if (page.value !== newPage) {
    page.value = newPage
    changed = true
  }

  if (changed) await fetchTodos()
}
</script>

<template>
  <div class="p-6">
    <div class="flex items-center justify-between gap-4">
      <Bs2Title title="Tableau de bord des tâches" subtitle="Gérez vos tâches" />
      <div class="flex gap-2">
        <TodoModal @created="onTodoCreated" />
      </div>
    </div>

    <div class="mt-6 card bg-base-100 shadow">
      <div class="card-body p-4 pt-2">
        <GridVue
          :list="todos"
          :cols="gridColumns"
          :get-row-height="(_params: any) => 48"
          :use-server-side-pagination="true"
          :pagination-page-size="pageSize"
          :pagination-total-rows="totalTodos"
          :current-page="page"
          :total-pages="totalPages"
          @onPaginationChanged="onPaginationChanged"
          @onSortChanged="onSortChanged"
        >
          <template #title>
            <h2 class="card-title">Liste des tâches</h2>
          </template>
          <template #filter>
            <input
              v-model="filters.search"
              type="text"
              placeholder="Rechercher..."
              class="input input-sm input-bordered flex-1 min-w-45"
            />
            <select v-model="filters.type" class="select select-sm select-bordered">
              <option value="">Tous les types</option>
              <option value="TASK">Tâche</option>
              <option value="REMINDER">Rappel</option>
              <option value="CALL">Appel</option>
              <option value="EMAIL">Email</option>
              <option value="MEETING">Réunion</option>
              <option value="FOLLOW_UP">Suivi</option>
            </select>
            <select v-model="filters.status" class="select select-sm select-bordered">
              <option value="">Tous les statuts</option>
              <option value="PENDING">En attente</option>
              <option value="COMPLETED">Terminé</option>
              <option value="CANCELLED">Annulé</option>
            </select>
            <select v-model="filters.priority" class="select select-sm select-bordered">
              <option value="">Toutes les priorités</option>
              <option value="LOW">Basse</option>
              <option value="MEDIUM">Moyenne</option>
              <option value="HIGH">Haute</option>
              <option value="URGENT">Urgente</option>
            </select>

            <div class="flex items-center gap-1">
              <label class="text-sm text-nowrap">Échéance :</label>
              <input
                v-model="filters.dueDateFrom"
                type="date"
                class="input input-sm input-bordered"
              />
              <span class="text-sm">→</span>
              <input
                v-model="filters.dueDateTo"
                type="date"
                class="input input-sm input-bordered"
              />
            </div>

            <ColumnSelector :columns="allColumns" cookie-name="todos-columns-visibility" @columns-changed="onColumnsChanged" />
          </template>
        </GridVue>
      </div>
    </div>

    <!-- Modal de visualisation/édition -->
    <TodoModal v-if="selectedTodo" :todo="selectedTodo" :show-modal="showModal" @close="closeModal" @updated="onTodoUpdated" @complete-and-copy="onCompleteAndCopy" />

    <!-- Modal de création par copie -->
    <TodoModal v-if="copyTodoData" :todo="copyTodoData" mode="create" :show-modal="showCopyModal" @close="closeCopyModal" @created="onCopyCreated" />
  </div>
</template>
<style scoped></style>
